import React from 'react';
import { StreamingOverviewResponse } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../common/types';
import { StreamingSection } from './streamingPaths';
import { isSectionAvailable } from './streamingSources';
import { OverviewSection } from './sections/OverviewSection';
import { TracksSection } from './sections/TracksSection';
import { TrackDetailSection } from './sections/TrackDetailSection';
import { DimensionSection } from './sections/DimensionSection';
import { PlaylistsSection } from './sections/PlaylistsSection';
import { EarningsSection } from './sections/EarningsSection';
import { AudienceSection } from './sections/audience/AudienceSection';
import { PlaySourcesSection } from './sections/playSources/PlaySourcesSection';
import { ShortVideoSection } from './sections/shortVideo/ShortVideoSection';
import { AcceleratorSection } from './sections/accelerator/AcceleratorSection';

interface StreamingSectionBodyProps {
  section: StreamingSection;
  accountId: number;
  overview: StreamingOverviewResponse;
  off: string[];
  trackKey: string | undefined;
  onNavigate: (path: string) => void;
  onOpenCatalog: () => void;
}

/**
 * The body of one section. Stores and Countries show when a connected source gives them (a direct
 * portal is its own store; a distributor reports many stores). Without one the screen shows only the
 * line that links to Catalog. Earnings shows its own card (design e3). Audience, Play sources, Short
 * video and Accelerator show their own empty state and plan card (design streaming-sections e2, e4).
 */
const StreamingSectionBodyComponent: React.FC<StreamingSectionBodyProps> = ({
  section,
  accountId,
  overview,
  off,
  trackKey,
  onNavigate,
  onOpenCatalog,
}) => {
  switch (section) {
    case 'tracks':
      return <TracksSection accountId={accountId} overview={overview} onNavigate={onNavigate} />;
    case 'track':
      return trackKey === undefined ? (
        <TracksSection accountId={accountId} overview={overview} onNavigate={onNavigate} />
      ) : (
        <TrackDetailSection accountId={accountId} overview={overview} trackKey={trackKey} onNavigate={onNavigate} />
      );
    case 'stores':
    case 'countries':
      return isSectionAvailable(overview, section) ? (
        <DimensionSection
          accountId={accountId}
          overview={overview}
          dimension={section === 'stores' ? 'store' : 'country'}
          onNavigate={onNavigate}
        />
      ) : null;
    case 'audience':
      return <AudienceSection accountId={accountId} overview={overview} onOpenCatalog={onOpenCatalog} />;
    case 'playSources':
      return <PlaySourcesSection accountId={accountId} overview={overview} onOpenCatalog={onOpenCatalog} />;
    case 'shortVideo':
      return <ShortVideoSection accountId={accountId} overview={overview} onOpenCatalog={onOpenCatalog} />;
    case 'accelerator':
      return <AcceleratorSection accountId={accountId} overview={overview} onOpenCatalog={onOpenCatalog} />;
    case 'playlists':
      return <PlaylistsSection accountId={accountId} />;
    case 'earnings':
      return <EarningsSection accountId={accountId} overview={overview} onOpenCatalog={onOpenCatalog} />;
    case 'overview':
    default:
      return (
        <OverviewSection
          accountId={accountId}
          overview={overview}
          off={off}
          onNavigate={onNavigate}
          onOpenCatalog={onOpenCatalog}
        />
      );
  }
};

export const StreamingSectionBody = TypedMemo(StreamingSectionBodyComponent);
